if (!customElements.get("color-swatch")) {
  class ColorSwatch extends HTMLElement {
    constructor() {
      super();
      this.onSwatchActivation = this.onSwatchActivation.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.optionNodes = this.querySelectorAll("[data-value]");
      this.init();
    }

    disconnectedCallback() {
      if (!this.optionNodes) return;

      this.optionNodes.forEach((button) => {
        button.removeEventListener("mouseenter", this.onSwatchActivation);
        button.removeEventListener("focus", this.onSwatchActivation);
      });

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else if (typeof window.removeEventListener === "function") {
        window.removeEventListener("resize", this.onResize);
      }
    }

    init() {
      const productCard = this.closest(".product-card");
      if (!productCard) return;

      this.mainImage = productCard.querySelector(
        ".product-card__image--main img"
      );
      this.restNode = this.querySelector("[data-swatch-rest]");
      this.restCountNode = this.querySelector("[data-swatch-rest-count]");
      this.defaultImageAlt = this.mainImage ? this.mainImage.alt : "";
      this.getVariantData();
      this.initOptionSwatches();

      this.optionNodes.forEach((button) => {
        button.addEventListener("mouseenter", this.onSwatchActivation);
        button.addEventListener("focus", this.onSwatchActivation);
      });

      this.selected = this.querySelector('a[aria-selected="true"]');
      this.updateVisibleSwatches();
      this.setupResizeObserver();
    }

    setupResizeObserver() {
      if (typeof window.ResizeObserver === "function") {
        this.resizeObserver = new window.ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);
      } else if (typeof window.addEventListener === "function") {
        window.addEventListener("resize", this.onResize);
      }
    }

    onResize() {
      this.updateVisibleSwatches();
    }

    getConfiguredMaxSlots() {
      const parsedValue = Number.parseInt(this.dataset.maxSlots, 10);
      const maxSlots = Number.isFinite(parsedValue) ? parsedValue : 6;

      return Math.min(10, Math.max(3, maxSlots));
    }

    getTotalValues() {
      const parsedValue = Number.parseInt(this.dataset.totalValues, 10);

      return Number.isFinite(parsedValue)
        ? Math.max(0, parsedValue)
        : this.optionNodes.length;
    }

    getLayoutMeasurements() {
      const computedStyle =
        typeof window.getComputedStyle === "function"
          ? window.getComputedStyle(this)
          : null;
      const firstOption = this.optionNodes[0];
      const itemWidth = firstOption ? firstOption.offsetWidth : 0;
      const gap = computedStyle
        ? Number.parseFloat(computedStyle.columnGap || computedStyle.gap) || 0
        : 0;
      const paddingInline = computedStyle
        ? (Number.parseFloat(computedStyle.paddingLeft) || 0) +
          (Number.parseFloat(computedStyle.paddingRight) || 0)
        : 0;

      return {
        containerWidth: Math.max(0, this.clientWidth - paddingInline),
        itemWidth,
        gap,
      };
    }

    calculateSwatchVisibility(
      containerWidth,
      itemWidth,
      gap,
      configuredMax,
      totalValues
    ) {
      const maxSlots = Math.min(
        10,
        Math.max(3, Number.parseInt(configuredMax, 10) || 6)
      );
      const total = Math.max(0, Number.parseInt(totalValues, 10) || 0);
      let availableSlots = maxSlots;

      if (containerWidth > 0 && itemWidth > 0) {
        availableSlots = Math.floor(
          (containerWidth + gap) / (itemWidth + gap)
        );
      }

      const slotLimit = Math.min(maxSlots, Math.max(3, availableSlots));
      const hasOverflow = total > slotLimit;
      const visibleRealCount = hasOverflow
        ? Math.max(2, slotLimit - 1)
        : Math.min(total, slotLimit);

      return {
        slotLimit,
        visibleRealCount,
        hiddenCount: Math.max(0, total - visibleRealCount),
      };
    }

    updateVisibleSwatches() {
      if (!this.optionNodes || !this.optionNodes.length) return;

      const { containerWidth, itemWidth, gap } = this.getLayoutMeasurements();
      const totalValues = this.getTotalValues();
      const visibility = this.calculateSwatchVisibility(
        containerWidth,
        itemWidth,
        gap,
        this.getConfiguredMaxSlots(),
        totalValues
      );
      const visibleRealCount = Math.min(
        visibility.visibleRealCount,
        this.optionNodes.length
      );
      const hiddenCount = Math.max(0, totalValues - visibleRealCount);

      this.optionNodes.forEach((optionNode, index) => {
        optionNode.hidden = index >= visibleRealCount;
      });

      if (this.restNode) {
        this.restNode.hidden = hiddenCount === 0;
      }
      if (this.restCountNode) {
        this.restCountNode.textContent = `+${hiddenCount}`;
      }
    }

    initOptionSwatches() {
      this.optionNodes.forEach((optNode) => {
        const { optionPosition, value: optionValue } = optNode.dataset;
        const variant = this.getRepresentativeVariant(
          optionPosition,
          optionValue
        );
        if (!variant) return;

        this.setVariantUrl(optNode, variant.id);

        const variantImage = this.getVariantImage(variant);
        if (!variantImage) return;

        const imageSrc =
          typeof getSizedImageUrl === "function"
            ? getSizedImageUrl(variantImage.src, "900x")
            : variantImage.src;
        const imageSrcset =
          typeof getSrcset === "function" ? getSrcset(variantImage.src) : "";

        optNode.setAttribute("data-src", imageSrc);
        if (imageSrcset) {
          optNode.setAttribute("data-srcset", imageSrcset);
        }
        optNode.setAttribute(
          "data-alt",
          variantImage.alt || this.defaultImageAlt
        );

        if (optNode.dataset.useVariantImageSwatch === "true") {
          const swatchSrc =
            typeof getSizedImageUrl === "function"
              ? getSizedImageUrl(variantImage.src, "72x")
              : variantImage.src;
          optNode.style.backgroundImage = `url("${swatchSrc}")`;
        }
      });
    }

    getRepresentativeVariant(optionPosition, optionValue) {
      const normalizedValue = this.normalizeOptionValue(optionValue);
      const optionKey = `option${optionPosition}`;
      const matchingVariants = this.variantData.filter(
        (variant) =>
          this.normalizeOptionValue(variant[optionKey]) === normalizedValue
      );

      return (
        matchingVariants.find(
          (variant) =>
            this.isVariantAvailable(variant) && this.getVariantImage(variant)
        ) ||
        matchingVariants.find((variant) => this.getVariantImage(variant)) ||
        matchingVariants.find((variant) => this.isVariantAvailable(variant)) ||
        matchingVariants[0]
      );
    }

    normalizeOptionValue(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    }

    isVariantAvailable(variant) {
      return variant && variant.available !== false;
    }

    getVariantImage(variant) {
      if (!variant || !variant.featured_image || !variant.featured_image.src) {
        return null;
      }

      return variant.featured_image;
    }

    setVariantUrl(optNode, variantId) {
      if (!variantId) return;

      try {
        const url = new URL(optNode.href, window.location.href);
        url.searchParams.set("variant", variantId);
        optNode.href = url.toString();
      } catch (_error) {
        const separator = optNode.href.includes("?") ? "&" : "?";
        optNode.href = `${optNode.href}${separator}variant=${variantId}`;
      }
    }

    onSwatchActivation(event) {
      const target = event.currentTarget || event.target;
      if (!target) return;

      if (this.selected && this.selected !== target) {
        this.selected.removeAttribute("aria-selected");
      }
      target.setAttribute("aria-selected", "true");

      const { src, srcset } = target.dataset;
      if (this.mainImage && src) {
        this.mainImage.src = src;
        if (srcset) {
          this.mainImage.srcset = srcset;
        } else {
          this.mainImage.removeAttribute("srcset");
        }

        if (target.hasAttribute("data-alt")) {
          this.mainImage.alt = target.dataset.alt;
        }
      }

      this.selected = target;
    }

    getVariantData() {
      if (this.variantData) return this.variantData;

      try {
        const dataNode = this.nextElementSibling;
        const parsedData = dataNode ? JSON.parse(dataNode.textContent) : [];
        this.variantData = Array.isArray(parsedData) ? parsedData : [];
      } catch (_error) {
        this.variantData = [];
      }

      return this.variantData;
    }
  }
  customElements.define("color-swatch", ColorSwatch);
}
